import React, { useEffect, useState } from "react";
import { Radio, Calendar, Users, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { FourStars } from "../components/FourStars";

export default function LivePage() {
  const [current, setCurrent] = useState(null);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [c, all] = await Promise.all([
          api.get("/live-streams/current").catch(() => ({ data: {} })),
          api.get("/live-streams").catch(() => ({ data: [] })),
        ]);
        setCurrent(c.data?.id ? c.data : null);
        setUpcoming((all.data || []).filter((s) => s.status === "scheduled"));
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="live-page" className="bg-[#0A0F1A] text-white min-h-screen">
      <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-br from-[#0A1F44] via-[#0052CC] to-[#0A1F44]">
        <div className="absolute inset-0 stars-pattern opacity-15" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 text-center">
          <FourStars size={14} className="justify-center mb-4" />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#CE1126]/20 border border-[#CE1126]/40 text-[#FCA5A5] text-xs font-semibold uppercase tracking-[0.25em] mb-4">
            <Radio size={11} className={current ? "animate-pulse" : ""} /> {current ? "En direct maintenant" : "Pas de direct en cours"}
          </div>
          <h1 className="font-display text-4xl md:text-6xl leading-tight">
            {current ? current.title : "Diffusions A.F.B."}
          </h1>
          {current?.description && <p className="text-white/85 mt-5 max-w-2xl mx-auto">{current.description}</p>}
        </div>
      </section>

      {current ? (
        <section className="py-12">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border-2 border-[#D4AF37]/40 bg-black" data-testid="live-player">
              <iframe
                src={current.embed_url}
                title={current.title}
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </div>
            <div className="flex items-center justify-between mt-5 flex-wrap gap-4">
              <div className="flex items-center gap-3 text-sm text-white/80">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-[#CE1126] text-white">
                  <Radio size={10} className="animate-pulse" /> EN DIRECT
                </span>
                <span className="inline-flex items-center gap-1.5"><Users size={13} /> Diffusion publique</span>
              </div>
              <Link to="/dons" className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline text-sm font-semibold">
                <Heart size={14} fill="#D4AF37" /> Soutenir A.F.B.
              </Link>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 grid place-items-center mx-auto text-white/40">
              <Radio size={32} />
            </div>
            <h2 className="font-display text-2xl mt-6">Aucune diffusion en cours</h2>
            <p className="text-white/60 mt-3 max-w-lg mx-auto">
              Revenez prochainement pour suivre les prochains événements officiels A.F.B. en direct.
            </p>
          </div>
        </section>
      )}

      {upcoming.length > 0 && (
        <section className="py-16 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-semibold">
              <Calendar size={13} /> À venir
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">Prochaines diffusions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
              {upcoming.map((s) => (
                <div key={s.id} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-[#D4AF37]/40 transition-colors" data-testid={`upcoming-${s.id}`}>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                    <Calendar size={11} />
                    {s.scheduled_at ? new Date(s.scheduled_at).toLocaleString("fr-FR") : "Bientôt"}
                  </div>
                  <h3 className="font-display text-xl text-white mt-2">{s.title}</h3>
                  {s.description && <p className="text-sm text-white/70 mt-2 line-clamp-2">{s.description}</p>}
                  <div className="text-xs text-white/50 mt-3 capitalize">Plateforme : {s.platform}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
